import { X, Info, TrendUp, ShieldWarning, Brain, Broadcast, Lightning, Target, ChartLineUp, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export function AboutModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      data-testid="about-modal"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl mx-auto my-8 bg-[#0A0A0A] border border-white/10 h-fit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#0f0f0f] border-b border-white/10 px-6 py-3 flex items-center gap-3">
          <Info size={18} className="text-[#F5C518]" />
          <h2 className="font-display text-lg font-bold tracking-widest uppercase text-white">
            About Longbeam
          </h2>
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/40 ml-2">
            v2.1 · Self-Evolving + Cryptometer Alpha
          </span>
          <Button
            data-testid="close-about-modal"
            onClick={onClose}
            variant="outline"
            size="sm"
            className="ml-auto rounded-none border-white/15 bg-transparent hover:bg-white/5 text-white"
          >
            <X size={14} />
          </Button>
        </div>

        <div className="p-6 space-y-6 text-white/80 text-[13px] leading-relaxed">
          <Section icon={<TrendUp className="text-[#00FF66]" size={16} />} title="What This Bot Does">
            <p>
              Longbeam scans USDT-margined perpetual futures on <b>OKX</b> (cross-checked
              against WEEX / BLOFIN availability) plus <b>MEXC-only</b> pairs where OKX doesn&apos;t
              list them, and hunts for <b>high-conviction long and short entries</b> across
              four modes:
            </p>
            <ul className="mt-2 list-disc ml-5 space-y-1">
              <li><b>Scalp</b> — 5m timeframe, 1h HTF, 4-8h horizon, 20-100× leverage.</li>
              <li><b>Swing</b> — 1h TF, 4h HTF, 12-48h horizon, 10-40× leverage.</li>
              <li><b>Position</b> — 4h TF, 1D HTF, 3-14d horizon, 3-15× leverage.</li>
              <li><b>Macro</b> — 1D TF, 1W HTF, 2-8 week horizon, 3-15× leverage. Catches weekly trend flips (BTC halving cycles, macro rotations).</li>
            </ul>
            <p className="mt-2">
              It never places live orders — you review and execute manually on your exchange.
              <br />
              <span className="text-[#F5C518] font-mono text-[11px]">
                Universe: up to 150 pairs, min $2M/24h quote volume (thin books filtered out to avoid punishing slippage).
              </span>
            </p>
          </Section>

          <Section icon={<Sparkle className="text-[#F5C518]" size={16} />} title="Self-Evolving Indicators — NEW">
            <p>
              Longbeam <b>writes its own indicators</b>. Every 30 minutes it mines the last
              500 resolved trades for winning 1- and 2-condition patterns
              (e.g. <span className="text-white/60 italic">ema_stack ∧ adx≥30</span>,{" "}
              <span className="text-white/60 italic">rsi_bounce ∧ btc_corr_20≥0.6</span>), promotes
              them to <b>bonus criteria</b> that add up to +15 to every matching signal&apos;s
              score, and <b>auto-decays</b> any rule whose recent win rate slips.
            </p>
            <p className="mt-2">
              Each signal now carries a rich <b>feature-vector snapshot</b>: hour of day,
              day of week, 20-bar BTC correlation, and a spread estimate — richer inputs mean
              the miner discovers subtler patterns as your trade history grows. Every rule is
              human-readable, appears in the <b>Self-Evolving Indicators</b> panel with its
              win-rate, sample count, edge over baseline, and current bonus. You can toggle
              individual rules on/off or wipe the pool with the trash icon.
            </p>
          </Section>

          <Section icon={<Broadcast className="text-[#00FF66]" size={16} />} title="Cryptometer.io Alpha — NEW">
            <p>
              Longbeam now pulls <b>external market intelligence</b> from Cryptometer.io
              every 15 minutes and layers it on top of the technical confluence score.
              Seven sources feed the alpha pipeline:
            </p>
            <ul className="mt-2 list-disc ml-5 space-y-1">
              <li><b>Trend Indicator V3</b> — global buy vs sell pressure across all majors.</li>
              <li><b>Rapid Movements</b> — pump / dump list for the last 4 hours.</li>
              <li><b>Long/Shorts Data</b> — per-pair long vs short totals on binance_futures (contrarian).</li>
              <li><b>24h Pair Volume</b> — buy vs sell dollar volume on the exact pair.</li>
              <li><b>Volume Flow</b> — global money inflow / outflow into each asset (PAID plan).</li>
              <li><b>Liquidations</b> — per-pair long vs short liquidations (PAID; contrarian long-liqs = bullish).</li>
              <li><b>LS Ratio</b> — high-resolution crowd ratio on futures (PAID; contrarian).</li>
            </ul>
            <p className="mt-2">
              Each source emits an <b>alpha</b> in [-1, +1] with a confidence score. All sources
              are combined into a signed bonus (capped at ±10 points) applied to every
              candidate signal in its trade direction. <b>Weights auto-tune every hour</b> — if a
              source has correctly agreed with winning trades over 20+ resolved signals, its
              weight is bumped ×1.10 (max 2.5). If it disagrees with winners, it&apos;s cut ×0.70
              (floor 0.1) and can be auto-disabled. You can manually toggle any source or reset
              the weights from the Cryptometer panel.
            </p>
            <p className="mt-2 text-[#F5C518] font-mono text-[11px]">
              Free tier = 10,000 calls/month. Set{" "}
              <code>CRYPTOMETER_PAID_ENDPOINTS=true</code> in backend/.env if you upgrade to
              unlock the three PAID sources.
            </p>
          </Section>

          <Section icon={<Brain className="text-[#F5C518]" size={16} />} title="Self-Learning Engine (Bayesian Weight Tuner)">
            <p>
              Separate from rule mining, every resolved signal updates a per-criterion
              &quot;edge&quot; statistic (win-rate when hit − win-rate when missed). Criteria
              with positive edge get their weight boosted up to 1.4×; negative edge criteria
              get shrunk. Bayesian shrinkage prevents overreacting to small samples. Once a
              symbol has 30+ resolved trades, it develops its own per-pair weight profile
              (BTC and PEPE learn independently).
            </p>
            <p className="mt-2">
              The <b>Winner Profile</b> panel runs a 60-day backtest across the top 20 pairs
              and 4 modes to surface which criteria + which modes are actually generating
              positive edge right now. Click <b>Apply Suggested Weights</b> to bake the
              recommendations into the live scanner — no restart required.
            </p>
            <p className="mt-2">
              A <b>Walk-Forward Validation</b> report checks whether learned weights beat
              base weights out-of-sample. If OOS edge is negative, the learner is overfitting
              — trust the base weights until enough data accumulates.
            </p>
          </Section>

          <Section icon={<Target className="text-[#F5C518]" size={16} />} title="Core Metrics — What They Mean">
            <ul className="space-y-2 list-none">
              <Metric label="Win Rate">
                % of resolved signals that hit TP1 (partial win) or TP2 (full win). TP1
                counts as a win — that&apos;s how pros trade. Green ≥65%, yellow 50-65%, red &lt;50%.
              </Metric>
              <Metric label="Profit Factor">
                Gross profit ÷ gross loss. <b>&gt;1.5 = healthy edge</b>, &gt;2.0 = excellent.
                Below 1 means you&apos;re losing money regardless of win rate.
              </Metric>
              <Metric label="Expectancy R">
                Expected profit per trade in R-multiples (1R = your risk unit).
                <b> Any positive number means the system has real edge.</b>
              </Metric>
              <Metric label="Score">
                Weighted confluence score 0-100. Only signals ≥ mode threshold fire. Includes
                bonus points from any active self-evolving indicators that match.
              </Metric>
              <Metric label="ADX(14)">
                Trend strength. <b>&gt;25 = strong trend</b>, 20-25 = OK, &lt;20 = choppy.
                Each mode has its own ADX threshold.
              </Metric>
              <Metric label="Volatility Regime & Market Regime">
                Volatility: Low / Normal / High vs the pair&apos;s own 100-bar median ATR%.
                Market regime: trend / range / transition — <b>scalp mode auto-rejects range</b>
                because 5m chop destroys tight stops.
              </Metric>
              <Metric label="Lorentzian Classification (ML)">
                A kNN-style pattern classifier borrowed from jdehorty&apos;s TradingView indicator.
                Compares the current bar&apos;s feature vector (RSI, ADX, WT, CCI) against 2000
                historical bars using Lorentzian distance, votes with the K=8 nearest neighbours.
                Scalp mode requires an aligned Lorentzian vote when win rate slips below 55%.
              </Metric>
              <Metric label="Weekly Structure Break">
                Detects when the current bar takes out the 8-week high (bull) or 8-week low
                (bear) — a hard-earned macro criterion worth +18 weight. Fires the biggest
                trends.
              </Metric>
              <Metric label="Trigger History">
                Live table of every recent signal with entry status: <b>safe</b> (price still
                near entry), <b>too_late</b> (price has moved &gt;30% toward TP1), <b>tp1_hit</b>,
                <b> sl_hit</b>, and a <b>BE</b> badge if stop moved to break-even after TP1.
              </Metric>
            </ul>
          </Section>

          <Section icon={<ShieldWarning className="text-[#FF3B30]" size={16} />} title="Risk Guardrails">
            <ul className="space-y-2 list-none">
              <Metric label="Break-Even After TP1">
                When a trade hits TP1 (partial), the stop-loss automatically slides to entry.
                A losing trade after TP1 hit is capped at ~0R — you can&apos;t give back the win.
                Visible as a <b>BE</b> badge on the Trigger History row.
              </Metric>
              <Metric label="Auto-Tightening Mode Overrides">
                If any mode&apos;s rolling win rate drops below 55%, the bot automatically
                tightens that mode&apos;s min_score by +5. Scalp is stricter: below 55%, it
                forces min_score=70 <b>AND</b> requires Lorentzian alignment. When win rate
                recovers to ≥65%, the override is auto-cleared. See <b>Winner Profile</b>{" "}
                for current overrides.
              </Metric>
              <Metric label="Kill Switch">
                Auto-pauses new signals for 60 minutes if rolling 24h leveraged PnL ≤ −8%
                OR 5 consecutive losses.
              </Metric>
              <Metric label="Correlation Guard">
                Skips a new signal if a &gt;0.85-correlated pair already has an active
                same-direction signal in the same mode. Prevents BTC + ETH + SOL long
                simultaneously = one trade in disguise.
              </Metric>
              <Metric label="Liquidation-Aware Leverage">
                Reduces suggested leverage so SL sits at least 1.5× closer to entry
                than liquidation price. Prevents wicks from liquidating you before SL fires.
              </Metric>
              <Metric label="Liquidity Filter">
                Any pair below <b>$2M/24h</b> quote volume is dropped from the universe —
                thin books mean punishing spread + slippage that would eat the edge.
              </Metric>
              <Metric label="Realistic Backtest Costs">
                The backtester now bakes in taker fees, funding cost (~0.01%/8h), and
                adaptive slippage that widens in high-volatility regimes. What you see in
                the backtest is what you&apos;ll get live.
              </Metric>
              <Metric label="News Blackout (ForexFactory)">
                Auto-pauses entries during CPI, FOMC, NFP, and any custom event you add.
                Default window: ±60min (CPI) / ±90min (FOMC). Live feed via
                nfs.faireconomy.media, refreshed every 6h.
              </Metric>
              <Metric label="Crowded-Side Filter">
                Skips signals piling onto the already-crowded side of the book (funding rate
                extreme) — those are squeeze fuel, not trend continuation.
              </Metric>
            </ul>
          </Section>

          <Section icon={<Broadcast className="text-[#00FF66]" size={16} />} title="Telegram Alerts">
            <p>
              High-conviction signals (score ≥ 72 by default) are pushed to Telegram.
              To subscribe: message <code className="text-[#F5C518]">@your_bot</code> with
              <code className="text-[#F5C518]"> /start </code>, then click &quot;Discover&quot; in the
              Telegram panel — the bot will auto-register your chat ID. You can adjust the
              score threshold or unsubscribe at any time.
            </p>
          </Section>

          <Section icon={<Lightning className="text-[#F5C518]" size={16} />} title="How to Use — Best Practice">
            <ol className="list-decimal ml-5 space-y-1.5">
              <li>
                <b>Watch Swing mode first.</b> Highest consistent win rate (~68-72%).
                Scalp fires more often but wins less. Macro fires rarely but wins big.
              </li>
              <li>
                <b>Only take signals with score ≥ 70.</b> Below that, edge shrinks fast. If
                the signal has <b>Evolved +N</b> badges, weight those higher — the miner
                already vetted the pattern on prior wins.
              </li>
              <li>
                <b>Use the Trigger History.</b> A signal marked <b>too_late</b> means price
                has moved so far you&apos;re paying for someone else&apos;s move — pass on it.
              </li>
              <li>
                <b>Respect the leverage clamp.</b> The clamped leverage is safer than the raw
                recommendation — never override upward.
              </li>
              <li>
                <b>Use 50/50 partial exits.</b> Close 50% at TP1, the bot moves stop to
                break-even automatically. Let the rest run to TP2.
              </li>
              <li>
                <b>Trust the auto-tightening.</b> If Winner Profile shows scalp overridden
                to min_score=70, it means the last 20+ scalps under-performed. Don&apos;t bypass it.
              </li>
              <li>
                <b>Add your own news events.</b> Token unlocks, exchange maintenance —
                the bot will auto-pause around them.
              </li>
              <li>
                <b>Let it evolve.</b> The Self-Evolving Indicators panel improves as more
                trades resolve. Once you cross 100+ resolved trades, expect 5-15 active
                rules bumping scores on the highest-quality setups.
              </li>
            </ol>
          </Section>

          <Section icon={<ChartLineUp className="text-[#00FF66]" size={16} />} title="Data Source & Limitations">
            <ul className="space-y-1 list-disc ml-5">
              <li>Market data: OKX USDT-margined swaps + MEXC fallback (public APIs, no keys).</li>
              <li>WEEX / BLOFIN availability is a best-effort static mapping — verify on your exchange before trading.</li>
              <li>Fair Value Gaps detected on 1m and 5m timeframes. Searchable pair picker in the FVG table.</li>
              <li>Backtest includes taker fees + funding cost + adaptive slippage — matches live execution closely.</li>
              <li>No exchange order routing — you enter every trade manually.</li>
              <li>Not financial advice. High-leverage crypto trading can and will lose 100% of your risk capital.</li>
            </ul>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h3 className="font-display text-sm font-bold tracking-widest uppercase text-white">{title}</h3>
      </div>
      <div className="text-white/70">{children}</div>
    </div>
  );
}

function Metric({ label, children }) {
  return (
    <li className="border-l-2 border-white/10 pl-3">
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#F5C518]">{label}</div>
      <div className="text-white/70 mt-0.5">{children}</div>
    </li>
  );
}
